import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Download, Save, Loader2, Sheet, Plus, ClipboardCheck, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CreateBriefDialog } from './briefs/CreateBriefDialog';
import { BriefDetailSheet } from './briefs/BriefDetailSheet';
import { api as briefApi, AE_ROLES, briefToForm, errMsg } from './briefs/briefApi';
import { Sidebar } from '../../components/Sidebar';
import { BriefForm, EMPTY_BRIEF } from './BriefForm';
import { ShortlistTable } from './ShortlistTable';
import { exportShortlistCsv } from './exportCsv';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const num = (v) => (v === '' || v == null ? null : Number(v));

const toPayload = (b) => ({
  ...b,
  min_followers: num(b.min_followers),
  max_followers: num(b.max_followers),
  budget_per_creator: num(b.budget_per_creator),
  max_creators: num(b.max_creators) || 30,
  gender: b.gender === 'any' ? null : b.gender,
  location: null,
});

const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const SHEET_BTN = 'rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-[#0F9D58] hover:bg-[#0B8043] text-white disabled:opacity-40 transition-colors';

export default function CampaignIdeation() {
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ cities: [], states: [] });
  const [savedView, setSavedView] = useState(false);
  const [savedBriefId, setSavedBriefId] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(null);
  const [costCols, setCostCols] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isAE = AE_ROLES.includes(user?.role);
  const [aeBriefs, setAeBriefs] = useState([]);
  const [activeBrief, setActiveBrief] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [savingToBrief, setSavingToBrief] = useState(false);

  const loadAeBriefs = useCallback(() => {
    if (isAE) briefApi.get('').then((d) => {
      setAeBriefs(d.briefs);
      setActiveBrief((a) => (a ? d.briefs.find((b) => b.id === a.id) || null : a));
    }).catch(() => {});
  }, [isAE]);
  useEffect(loadAeBriefs, [loadAeBriefs]);

  const loadBriefFilters = (b) => {
    setActiveBrief(b);
    setBrief(briefToForm(b, EMPTY_BRIEF));
    setRows(null);
    toast.success(`Loaded filters from ${b.brand_name} brief`, { description: 'Run suggestions, then Save to Brief.' });
  };
  const saveToBrief = async () => {
    setSavingToBrief(true);
    try {
      const creators = selected.map((r) => ({ creator_id: r.creator_id, fit_score: r.fit_score === '' ? null : r.fit_score, manager_note: r.manager_note || '' }));
      const d = await briefApi.post(`/${activeBrief.id}/shortlist`, { creators });
      toast.success(`Saved to ${activeBrief.brand_name}: ${d.added} added`, { description: `${d.already_in_brief} already in brief · ${d.shortlisted_count} shortlisted total` });
      loadAeBriefs();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save to brief'));
    } finally {
      setSavingToBrief(false);
    }
  };

  const exportToSheets = async (id) => {
    setSheetBusy(id);
    const win = window.open('', '_blank');
    try {
      const { data } = await axios.post(`${API_URL}/api/ideation/briefs/${id}/export-sheet`, {}, { withCredentials: true });
      toast.success('Sheet created! Opening...', { description: `${data.rows} creators · ${data.title}` });
      if (win) win.location.href = data.sheet_url; else window.open(data.sheet_url, '_blank', 'noopener');
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || 'Could not create sheet — check Google connection', { duration: 8000 });
    } finally {
      setSheetBusy(null);
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/ideation/filters`, { withCredentials: true }).then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/ideation/generate`, toPayload(brief), { withCredentials: true });
      setRows(data.creators.map((c) => ({ ...c, selected: true, manager_note: '' })));
      setTotal(data.total_matches);
      setCostCols(data.cost_columns);
      setSavedView(false);
      setSavedBriefId(null);
      toast.success(`${data.total_matches} creators match · showing top ${data.returned}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  const openBrief = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ideation/briefs/${id}`, { withCredentials: true });
      setBrief({ ...EMPTY_BRIEF, ...data.brief_inputs, gender: data.brief_inputs.gender || 'any', city: asList(data.brief_inputs.city), state: asList(data.brief_inputs.state) });
      setCostCols(data.cost_columns);
      setRows(data.creators.map((c) => ({ ...c, selected: true })));
      setTotal(data.creators.length);
      setSavedView(true);
      setSavedBriefId(id);
    } catch {
      toast.error('Could not open brief');
    }
  };

  useEffect(() => {
    const savedId = params.get('saved');
    const briefId = params.get('brief');
    if (!savedId && !briefId) return;
    setParams({}, { replace: true });
    if (savedId) openBrief(savedId);
    else briefApi.get(`/${briefId}`).then(loadBriefFilters).catch((e) => toast.error(errMsg(e, 'Could not load brief')));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = (rows || []).filter((r) => r.selected);
  const onRowChange = (id, patch) => setRows((rs) => rs.map((r) => (r.creator_id === id ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const creators = selected.map((r) => ({ creator_id: r.creator_id, fit_score: r.fit_score === '' ? null : r.fit_score, manager_note: r.manager_note || '', pitch_priority: r.pitch_priority }));
      const { data } = await axios.post(`${API_URL}/api/ideation/save-brief`, { brief_inputs: toPayload(brief), creators }, { withCredentials: true });
      toast.success(`Saved brief with ${data.creator_count} creators`, { description: 'Find it any time under Saved Briefs.', action: { label: 'View Saved Briefs', onClick: () => navigate('/ideation/saved-briefs') } });
      setSavedBriefId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="ideation-page">
        <div className="max-w-[1400px] mx-auto space-y-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
              <Sparkles className="text-[#FF451A]" size={30} /> Brief Suggestions
            </h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Enter a brand brief and get a ranked creator shortlist from the roster.</p>
          </div>

          {isAE && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={() => setCreateOpen(true)} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#FF451A] hover:bg-[#E63E17] text-white transition-colors" data-testid="create-brief-btn"><Plus size={13} /> Create Brief</button>
                {activeBrief && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#FF451A]/10 text-[#FF451A] text-xs font-medium pl-3 pr-1 py-1" data-testid="active-brief-banner">
                    <ClipboardCheck size={12} /> Working on: {activeBrief.brand_name} · {activeBrief.deliverable}
                    <button onClick={() => setDetailId(activeBrief.id)} className="underline" data-testid="active-brief-open">Open</button>
                    <button onClick={() => setActiveBrief(null)} className="rounded-full p-1 hover:bg-[#FF451A]/20" data-testid="active-brief-clear"><X size={11} /></button>
                  </div>
                )}
              </div>
            </div>
          )}

          <BriefForm brief={brief} setBrief={setBrief} onSubmit={generate} loading={loading} filters={filters} />

          {rows && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-[#0F0F0F] dark:text-white" data-testid="ideation-results-summary">
                  {savedView ? <>Saved brief · <span className="font-bold">{rows.length}</span> creators</> : <><span className="font-bold">{total}</span> matching creators · showing <span className="font-bold">{rows.length}</span></>} · <span className="font-bold text-[#FF451A]">{selected.length}</span> selected
                </div>
                <div className="flex gap-2">
                  {activeBrief && !savedView && (
                    <button onClick={saveToBrief} disabled={savingToBrief || !selected.length || activeBrief.status === 'Closed'} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#0F0F0F] dark:bg-white text-white dark:text-[#0F0F0F] hover:opacity-90 disabled:opacity-40 transition-opacity" data-testid="save-to-brief-btn">
                      {savingToBrief ? <Loader2 size={13} className="animate-spin" /> : <ClipboardCheck size={13} />} Save to Brief
                    </button>
                  )}
                  <button onClick={() => exportShortlistCsv(selected, brief.brand_name, costCols)} disabled={!selected.length} className="rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-black/[0.12] dark:border-white/[0.08] bg-white dark:bg-[#1A1D24] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/40 disabled:opacity-40 transition-colors" data-testid="ideation-export-csv-btn">
                    <Download size={13} /> Export CSV
                  </button>
                  {savedBriefId && (
                    <button onClick={() => exportToSheets(savedBriefId)} disabled={sheetBusy === savedBriefId} className={`${SHEET_BTN} px-4 py-2`} data-testid="ideation-export-sheet-btn">
                      {sheetBusy === savedBriefId ? <Loader2 size={13} className="animate-spin" /> : <Sheet size={13} />} Export to Sheets
                    </button>
                  )}
                  <button onClick={save} disabled={saving || !selected.length} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#FF451A] hover:bg-[#E63E17] text-white disabled:opacity-40 transition-colors" data-testid="ideation-save-brief-btn">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Brief
                  </button>
                </div>
              </div>
              {rows.length ? <ShortlistTable rows={rows} onRowChange={onRowChange} costCols={costCols} /> : (
                <div className="text-center py-12 text-sm text-[#6B6B6B] dark:text-zinc-400 border border-dashed border-black/[0.12] dark:border-white/10 rounded-2xl" data-testid="ideation-empty">No creators match this brief. Try widening the filters.</div>
              )}
            </div>
          )}
        </div>
      </div>
      {isAE && <CreateBriefDialog open={createOpen} onOpenChange={setCreateOpen} filters={filters} onCreated={(b) => { setCreateOpen(false); loadAeBriefs(); loadBriefFilters(b); }} />}
      {isAE && <BriefDetailSheet briefId={detailId} onClose={() => setDetailId(null)} onChanged={loadAeBriefs} onLoad={loadBriefFilters} />}
    </div>
  );
}
